/**
 * Hook: fetches a single article by slug.
 *
 * Returns `notFound: true` (vs an `error`) on a 404 so the UI can render
 * the empty-state component instead of a generic error banner.
 */

import { useEffect, useState } from "react";

import { ResearchArticleDetail } from "../../../models/Research";
import { useLeagueApi } from "../../../data/LeagueEnvironmentProvider";

export interface UseArticleResult {
  loading: boolean;
  error: string | null;
  notFound: boolean;
  article: ResearchArticleDetail | null;
}

export function useArticle(slug: string | undefined): UseArticleResult {
  const api = useLeagueApi();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [article, setArticle] = useState<ResearchArticleDetail | null>(null);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      setNotFound(true);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);
    setArticle(null);

    api
      .getResearchArticle(slug)
      .then((resp) => {
        if (cancelled) return;
        setArticle(resp.data);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        const msg =
          err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to load article";
        setError(msg);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, slug]);

  return { loading, error, notFound, article };
}
